"use client";

import Link from "next/link";
import { ArrowRight, Award, BookOpen, GitBranch } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { leadership as data } from "@/lib/data/about";

const introIcons = [BookOpen, Award, GitBranch] as const;

export function Leadership() {
  return (
    <Section id="leadership" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-violet)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {data.thoughtLeadership.map((row, i) => {
            const Icon = introIcons[i % introIcons.length]!;
            return (
              <Reveal key={row.heading} delay={i * 80}>
                <div className="glass rounded-2xl p-6 h-full">
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: "color-mix(in oklch, var(--color-violet) 18%, transparent)",
                      boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-violet) 35%, transparent)",
                    }}
                  >
                    <Icon className="h-5 w-5 text-[var(--color-violet)]" />
                  </span>
                  <h3 className="mt-4 text-lg">{row.heading}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{row.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={300}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Recognition</div>
            <div className="mt-5 space-y-3">
              {data.recognition.map((r, i) => (
                <div
                  key={`${r.source}-${r.year}`}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-[rgb(var(--border)/0.04)] transition-colors"
                >
                  <div className="text-2xl font-bold text-[var(--color-amber)] w-16 shrink-0">{r.year}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{r.recognition}</div>
                    <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5">{r.source}</div>
                  </div>
                  <span
                    className="grid place-items-center h-8 w-8 rounded-full shrink-0 text-[10px] font-bold"
                    style={{
                      background: "color-mix(in oklch, var(--color-amber) 18%, transparent)",
                      color: "var(--color-amber)",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={400}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
